import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function ParallaxHero({
  banner = "/assets/hero-bg.jpg",
  title,
  subtitle,
  ctaText,
  onCtaClick,
  children,
}) {
  const { t } = useTranslation();
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const y = window.scrollY;
      // ปรับ parallax ให้สมูทขึ้น
      el.style.transform = `translateY(${Math.min(y * 0.3, 200)}px)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="relative h-[75vh] md:h-[85vh] min-h-[500px] w-full overflow-hidden bg-[#5b4a3e]">
      {/* Background Image */}
      <div
        className="absolute left-0 w-full h-[100%] bg-center bg-cover scale-110 will-change-transform z-0"
        style={{ backgroundImage: `url(${banner})` }}
        ref={ref}
      />

      {/* Overlay Gradient - ปรับให้เข้มขึ้นเล็กน้อยเพื่อให้ตัวหนังสืออ่านง่าย */}
      <div className="absolute inset-0 bg-black/20 z-10" />

      {/* Bottom Gradient Fade - เชื่อมรอยต่อกับพื้นหลังเว็บ */}
      <div className="absolute bottom-0 left-0 w-full h-64 bg-gradient-to-t from-[#fcfaf8] via-[#fcfaf8]/20 to-transparent z-10" />

      {/* Content Area (Title & Subtitle) */}
      <div className="relative z-20 h-full flex flex-col items-center justify-center text-center px-6 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h1 className="text-4xl md:text-7xl font-bold tracking-tight text-white mb-6 drop-shadow-2xl italic">
            {title || t("hero.title")}
          </h1>

          <p className="max-w-3xl mx-auto text-lg md:text-2xl text-white/95 font-medium leading-relaxed mb-10 drop-shadow-lg">
            {subtitle || t("hero.subtitle")}
          </p>

          {ctaText && (
            <button
              onClick={onCtaClick}
              className="group relative bg-[#d8653b] hover:bg-[#bf5934] text-white px-10 py-4 rounded-full text-lg font-bold shadow-xl transition-all hover:scale-105 active:scale-95"
            >
              <span className="relative z-10">{ctaText}</span>
              <div className="absolute inset-0 rounded-full bg-white/20 scale-0 group-hover:scale-100 transition-transform duration-300"></div>
            </button>
          )}
        </motion.div>
      </div>

      {/* ✅ ส่วนที่เพิ่ม: Container สำหรับกล่องค้นหา (Search Box) 
          ใช้ absolute bottom-0 และ translate-y-1/2 เพื่อให้กล่องอยู่กึ่งกลางขอบล่างพอดี */}
      <div className="absolute bottom-20 left-0 w-full z-30 transform translate-y-1/2">
        <div className="max-w-4xl mx-auto px-4">
          {children}
        </div>
      </div>
    </section>
  );
}